import { useState } from "react";
import { Share2, Check } from "lucide-react";
import SmartImage from "@/components/ui/SmartImage";
import { useT } from "@/i18n/useLocale";

interface BriefHeaderProps {
  issueNumber: number;
  weekStart: string;
  weekEnd: string;
  headline: string;
  teaser: string;
  leadImage?: string | null;
  storyCount?: number;
  readMinutes?: number;
}

const fmt = (s: string) =>
  new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function BriefHeader({
  issueNumber,
  weekStart,
  weekEnd,
  headline,
  teaser,
  leadImage,
  storyCount = 0,
  readMinutes = 6,
}: BriefHeaderProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: headline, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* dismissed */
    }
  };

  const meta = [
    `Issue #${issueNumber}`,
    `${fmt(weekStart)} – ${fmt(weekEnd)}, ${new Date(weekEnd).getFullYear()}`,
    storyCount ? `${storyCount} stories` : null,
    `${readMinutes} min read`,
  ].filter(Boolean) as string[];

  return (
    <header className="mb-14">
      {leadImage ? (
        <div className="relative overflow-hidden rounded-3xl border border-border">
          <SmartImage
            src={leadImage}
            w={1400}
            h={720}
            priority
            alt=""
            className="h-[46vw] max-h-[520px] min-h-[280px] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/55 to-foreground/10" />
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10">
            <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.18em] text-background/80">
              {meta.map((m, i) => (
                <span key={m} className="flex items-center gap-3">
                  {i > 0 && <span className="opacity-40">·</span>}
                  {m}
                </span>
              ))}
            </div>
            <h1 className="max-w-4xl font-display text-3xl leading-[1.05] text-background md:text-5xl lg:text-6xl">
              {headline}
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-background/80 md:text-lg">{teaser}</p>
          </div>
          <button
            onClick={share}
            aria-label={t("auto.share-this-issue-c878")}
            className="absolute right-4 top-4 flex items-center gap-2 rounded-full border border-background/30 bg-background/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-background backdrop-blur transition-colors hover:bg-background/25"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Share"}
          </button>
        </div>
      ) : (
        <div className="border-b border-border pb-8">
          <div className="mb-3 flex flex-wrap items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {meta.map((m, i) => (
              <span key={m} className="flex items-center gap-3">
                {i > 0 && <span className="opacity-40">·</span>}
                {m}
              </span>
            ))}
          </div>
          <h1 className="mb-4 font-display text-4xl leading-[1.05] text-foreground md:text-5xl lg:text-6xl">
            {headline}
          </h1>
          <p className="max-w-3xl text-lg text-muted-foreground md:text-xl">{teaser}</p>
        </div>
      )}
    </header>
  );
}
